import React, { useState } from "react";
import toast from "react-hot-toast";
import CustomToast from "../components/common/CustomToast";
import {
  useDealers,
  useDealerStats,
  useUpdateDealerContact,
  useUpdateStoreStatus,
  useUpdateStoreImages,
  useUpdateStoreCategory,
  useActivateDealerByAdmin,
  useUpdateDealerMetrics,
  useChangeDealerPassword,
  useUpdateDealerZipCode,
  useUpdateStoreZipCode,
  useUpdateDealerCountry,
  useUpdateStoreCountry,
  useUpdateDealerSponsor,
} from "../hooks/useDealers";
import { useLocalStorage } from "../hooks/useLocalStorage";
import SearchBar from "../components/common/SearchBar";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Pagination from "../components/common/Pagination";
import { Dealer, apiService } from "../services/api";
import { useSystemCategories } from "../hooks/useCategories";
import {
  FunnelIcon,
  BuildingStorefrontIcon,
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  Squares2X2Icon,
  ListBulletIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  TagIcon,
  ArrowDownTrayIcon,
  ChartBarIcon,
  ArrowPathIcon,
  KeyIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
} from "@heroicons/react/24/outline";

type ViewMode = "card" | "list";
const WEEKDAYS = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

type FilterType =
  | "all"
  | "active"
  | "inactive"
  | "pending"
  | "assigned"
  | "unassigned"
  | "missing_images";

const Dealers: React.FC = () => {
  const [selectedDealer, setSelectedDealer] = useState<Dealer | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Inline edit states
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [editEmail, setEditEmail] = useState("");
  const [editPhonePrefix, setEditPhonePrefix] = useState("");
  const [editPhoneNumber, setEditPhoneNumber] = useState("");

  const [showActivateConfirm, setShowActivateConfirm] = useState(false);
  const [addPrepaidTopup, setAddPrepaidTopup] = useState(false);

  // Password change states
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // ZipCode edit states
  const [isEditingCompanyZipCode, setIsEditingCompanyZipCode] = useState(false);
  const [editCompanyZipCode, setEditCompanyZipCode] = useState("");
  const [editingStoreZipCodeId, setEditingStoreZipCodeId] = useState<
    string | null
  >(null);
  const [editStoreZipCode, setEditStoreZipCode] = useState("");

  // Country edit states
  const [isEditingCompanyCountry, setIsEditingCompanyCountry] = useState(false);
  const [editCompanyCountry, setEditCompanyCountry] = useState("");
  const [editingStoreCountryId, setEditingStoreCountryId] = useState<
    string | null
  >(null);
  const [editStoreCountry, setEditStoreCountry] = useState("");

  // Store image edit states
  const [editingImageStoreId, setEditingImageStoreId] = useState<string | null>(
    null,
  );
  const [editCoverImage, setEditCoverImage] = useState("");
  const [editLogoImage, setEditLogoImage] = useState("");
  const [editingCategoryStoreId, setEditingCategoryStoreId] = useState<
    string | null
  >(null);
  const [editStoreCategory, setEditStoreCategory] = useState("");
  const [editStoreSubcategory, setEditStoreSubcategory] = useState("");

  const [isEditingDealerSponsorId, setIsEditingDealerSponsorId] =
    useState(false);
  const [editDealerSponsorId, setEditDealerSponsorId] = useState("");

  const updateContactMutation = useUpdateDealerContact();
  const updateStoreStatusMutation = useUpdateStoreStatus();
  const updateStoreImagesMutation = useUpdateStoreImages();
  const updateStoreCategoryMutation = useUpdateStoreCategory();
  const activateDealerMutation = useActivateDealerByAdmin();
  const changePasswordMutation = useChangeDealerPassword();
  const updateDealerZipCodeMutation = useUpdateDealerZipCode();
  const updateStoreZipCodeMutation = useUpdateStoreZipCode();
  const updateDealerCountryMutation = useUpdateDealerCountry();
  const updateStoreCountryMutation = useUpdateStoreCountry();
  const updateDealerSponsorMutation = useUpdateDealerSponsor();
  const [filter, setFilter] = useLocalStorage<FilterType>(
    "dealers-filter",
    "all",
  );
  const [searchQuery, setSearchQuery] = useLocalStorage<string>(
    "dealers-search-query",
    "",
  );
  const [viewMode, setViewMode] = useLocalStorage<ViewMode>(
    "dealers-view-mode",
    "card",
  );
  const [categoryFilter, setCategoryFilter] = useLocalStorage<string>(
    "dealers-category-filter",
    "",
  );
  const [sortBy, setSortBy] = useLocalStorage<string>(
    "dealers-sort-by",
    "totalTransactions",
  );
  const [sortOrder, setSortOrder] = useLocalStorage<"asc" | "desc">(
    "dealers-sort-order",
    "desc",
  );
  const [automationFilter, setAutomationFilter] = useLocalStorage<
    "" | "automation_only" | "non_automation"
  >("dealers-automation-filter", "");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(20);
  const { data: systemCategories } = useSystemCategories();
  const updateMetricsMutation = useUpdateDealerMetrics();

  const { data: dealersResponse, isLoading } = useDealers({
    limit: itemsPerPage,
    skip: (currentPage - 1) * itemsPerPage,
    search: searchQuery || undefined,
    status: ["active", "inactive", "pending"].includes(filter)
      ? filter
      : undefined,
    marketingFilter: ["assigned", "unassigned"].includes(filter)
      ? (filter as "assigned" | "unassigned")
      : undefined,
    imageFilter: filter === "missing_images" ? "missing_images" : undefined,
    categoryFilter: categoryFilter || undefined,
    sortBy,
    sortOrder,
    automationFilter: automationFilter || undefined,
  });

  const { data: stats } = useDealerStats();

  const dealers = dealersResponse?.data || [];
  const totalItems = dealersResponse?.total || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handleUpdateMetrics = async () => {
    try {
      await updateMetricsMutation.mutateAsync();
    } catch (error) {
      // toast already handled in hook
    }
  };

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [
    filter,
    searchQuery,
    categoryFilter,
    sortBy,
    sortOrder,
    automationFilter,
    setCurrentPage,
  ]);

  const handleViewDetails = (dealer: Dealer) => {
    setSelectedDealer(dealer);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedDealer(null);
    setIsEditingEmail(false);
    setIsEditingPhone(false);
    setShowActivateConfirm(false);
    setAddPrepaidTopup(false);
    setIsChangingPassword(false);
    setNewPassword("");
    setConfirmPassword("");
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    handleCancelEditImages();
    handleCancelEditCategory();
    setIsEditingCompanyZipCode(false);
    setEditingStoreZipCodeId(null);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  // Email edit handlers
  const handleStartEditEmail = () => {
    setEditEmail(selectedDealer?.email || "");
    setIsEditingEmail(true);
  };

  const handleCancelEditEmail = () => {
    setIsEditingEmail(false);
    setEditEmail("");
  };

  const handleSaveEmail = async () => {
    if (!selectedDealer?._id) return;
    try {
      await updateContactMutation.mutateAsync({
        dealerId: selectedDealer._id,
        contactData: { email: editEmail },
      });
      setSelectedDealer({ ...selectedDealer, email: editEmail });
      setIsEditingEmail(false);
    } catch (error: any) {
      // e.g. 409 EMAIL_IN_USE when a Family Account member or a pending
      // family invitation holds the address. The address itself is never
      // put into the toast.
      const message = error?.response?.data?.message;
      const text = Array.isArray(message)
        ? message.filter((part) => typeof part === "string").join("; ")
        : typeof message === "string"
          ? message
          : "";
      toast.custom(
        <CustomToast
          message={text.trim() || "Failed to update dealer email"}
          type="error"
        />,
        { duration: 5000 },
      );
    }
  };

  // Phone edit handlers
  const handleStartEditPhone = () => {
    setEditPhonePrefix(selectedDealer?.phonePrefix || "");
    setEditPhoneNumber(selectedDealer?.phoneNumber || "");
    setIsEditingPhone(true);
  };

  const handleCancelEditPhone = () => {
    setIsEditingPhone(false);
    setEditPhonePrefix("");
    setEditPhoneNumber("");
  };

  const handleSavePhone = async () => {
    if (!selectedDealer?._id) return;
    try {
      await updateContactMutation.mutateAsync({
        dealerId: selectedDealer._id,
        contactData: {
          phonePrefix: editPhonePrefix,
          phoneNumber: editPhoneNumber,
        },
      });
      setSelectedDealer({
        ...selectedDealer,
        phonePrefix: editPhonePrefix,
        phoneNumber: editPhoneNumber,
      });
      setIsEditingPhone(false);
    } catch (error) {
      console.error("Failed to update dealer phone number.");
    }
  };

  // Store status change handler
  const handleStoreStatusChange = async (
    storeId: string,
    newStatus: string,
  ) => {
    if (!selectedDealer?.dealerId) return;

    try {
      await updateStoreStatusMutation.mutateAsync({
        dealerId: selectedDealer.dealerId,
        storeId,
        status: newStatus as "active" | "pending" | "inactive",
      });

      // Update local state to reflect the change
      setSelectedDealer({
        ...selectedDealer,
        stores: selectedDealer.stores?.map((store) =>
          store.storeId === storeId ? { ...store, status: newStatus } : store,
        ),
      });

      toast.success(`Store status updated to ${newStatus}`);
    } catch (error) {
      console.error("Failed to update store status:");
      toast.error("Failed to update store status");
    }
  };

  // Activate dealer handler
  const handleActivateDealer = async () => {
    if (!selectedDealer?.dealerId) return;

    try {
      await activateDealerMutation.mutateAsync({
        dealerId: selectedDealer.dealerId,
        addPrepaidTopup,
      });

      setSelectedDealer({
        ...selectedDealer,
        activationStatus: "activated",
        activationDate: new Date().toISOString(),
        activationMethod: "manual",
      });

      setShowActivateConfirm(false);
      setAddPrepaidTopup(false);
      toast.success(
        `Dealer activated successfully${
          addPrepaidTopup ? " with 10€ prepaid topup" : ""
        }`,
      );
    } catch (error: any) {
      console.error("Failed to activate dealer:");
      toast.error(
        error?.response?.data?.message || "Failed to activate dealer",
      );
    }
  };

  const handleStartEditImages = (store: any) => {
    setEditingImageStoreId(store.storeId);
    setEditCoverImage(store.coverImage || "");
    setEditLogoImage(store.logoImage || "");
  };

  const handleCancelEditImages = () => {
    setEditingImageStoreId(null);
    setEditCoverImage("");
    setEditLogoImage("");
  };

  const handleSaveImages = async (storeId: string) => {
    if (!selectedDealer?.dealerId) return;
    try {
      await updateStoreImagesMutation.mutateAsync({
        dealerId: selectedDealer.dealerId,
        storeId,
        images: { coverImage: editCoverImage, logoImage: editLogoImage },
      });
      // Update local state
      setSelectedDealer({
        ...selectedDealer,
        stores: selectedDealer.stores?.map((s) =>
          s.storeId === storeId
            ? { ...s, coverImage: editCoverImage, logoImage: editLogoImage }
            : s,
        ),
      });
      handleCancelEditImages();
      toast.success("Store images updated");
    } catch (error) {
      console.error("Failed to update images:");
      toast.error("Failed to update store images");
    }
  };

  const getCategoryNameStr = (cat: any): string => {
    if (!cat) return "";
    if (typeof cat === "object" && cat !== null)
      return cat.name || cat.categoryName || "";
    return String(cat);
  };

  const handleStartEditCategory = (store: any) => {
    setEditingCategoryStoreId(store.storeId);
    setEditStoreCategory(getCategoryNameStr(store.storeCategory));
    setEditStoreSubcategory(getCategoryNameStr(store.storeSubcategory));
  };

  const handleCancelEditCategory = () => {
    setEditingCategoryStoreId(null);
    setEditStoreCategory("");
    setEditStoreSubcategory("");
  };

  const handleSaveCategory = async (storeId: string) => {
    if (!selectedDealer?.dealerId) return;
    try {
      await updateStoreCategoryMutation.mutateAsync({
        dealerId: selectedDealer.dealerId,
        storeId,
        data: {
          storeCategory: editStoreCategory,
          storeSubcategory: editStoreSubcategory,
        },
      });
      setSelectedDealer({
        ...selectedDealer,
        stores: selectedDealer.stores?.map((s) =>
          s.storeId === storeId
            ? {
                ...s,
                storeCategory: editStoreCategory,
                storeSubcategory: editStoreSubcategory,
              }
            : s,
        ),
      });
      handleCancelEditCategory();
      toast.success("Store category updated");
    } catch (error) {
      console.error("Failed to update category:");
      toast.error("Failed to update store category");
    }
  };

  const [isExporting, setIsExporting] = useState(false);

  const handleExportDealers = async () => {
    setIsExporting(true);
    try {
      const res = await apiService.getDealers(
        { limit: 2000, skip: 0 },
        { timeout: 30000 },
      );
      const allDealers = res?.data || [];
      if (allDealers.length === 0) {
        toast.error("No dealers to export");
        return;
      }

      const getCategoryName = (cat: any) => {
        if (!cat) return "";
        if (typeof cat === "object" && cat !== null)
          return cat.name || cat.categoryName || "";
        return String(cat);
      };

      const rows = allDealers.map((d: Dealer) => ({
        dealerId: d.dealerId || "",
        companyName: d.companyName || "",
        email: d.email || "",
        phonePrefix: d.phonePrefix || "",
        phoneNumber: d.phoneNumber || "",
        country: d.country || "",
        currency: d.currency || "",
        status: d.status || "",
        registrationSource: d.createdByAutomation ? "AI/API" : "Manual",
        // Activation
        activationStatus: d.activationStatus || "",
        activationDate: d.activationDate || "",
        activationMethod: d.activationMethod || "",
        activationVoucherCode: d.activationVoucherCode || "",
        activationPaymentId: d.activationPaymentId || "",
        activationInvoiceId: d.activationInvoiceId || "",
        // VAT
        vatId: d.vatId || "",
        vatValidity: d.vatValidity !== undefined ? String(d.vatValidity) : "",
        // Sponsor & Backoffice
        sponsorId: d.sponsorId || "",
        sponsorType: d.sponsorType || "",
        dealerIdSalesBackoffice: d.dealerIdSalesBackoffice || "",
        dealerAgentIdBackoffice: d.dealerAgentIdBackoffice || "",
        qrLink: d.qrLink || "",
        latitude: d.latitude !== undefined ? String(d.latitude) : "",
        longitude: d.longitude !== undefined ? String(d.longitude) : "",
        totalStores: d.totalStores !== undefined ? String(d.totalStores) : "0",
        // Performance
        totalTransactions:
          d.totalTransactions !== undefined ? String(d.totalTransactions) : "0",
        totalSponsoredCustomers:
          d.totalSponsoredCustomers !== undefined
            ? String(d.totalSponsoredCustomers)
            : "0",
        // Contact Person
        contactPerson_salutation: d.contactPerson?.salutation || "",
        contactPerson_firstName: d.contactPerson?.firstName || "",
        contactPerson_lastName: d.contactPerson?.lastName || "",
        contactPerson_email: d.contactPerson?.email || "",
        contactPerson_phonePrefix: d.contactPerson?.phonePrefix || "",
        contactPerson_phoneNumber: d.contactPerson?.phoneNumber || "",
        // Company Address
        companyAddress_street: d.companyAddress?.street || "",
        companyAddress_housenumber: d.companyAddress?.housenumber || "",
        companyAddress_addressAddition: d.companyAddress?.addressAddition || "",
        companyAddress_zipCode: d.companyAddress?.zipCode || "",
        companyAddress_city: d.companyAddress?.city || "",
        companyAddress_country: d.companyAddress?.country || "",
        companyAddress_vatId: d.companyAddress?.vatId || "",
        // Store Information
        store_name: d.storeInformation?.storeName || "",
        store_storeId: d.storeInformation?.storeId || "",
        store_category: getCategoryName(d.storeInformation?.storeCategory),
        store_subcategory: getCategoryName(
          d.storeInformation?.storeSubcategory,
        ),
        store_cashbackRate:
          d.storeInformation?.cashbackRate !== undefined
            ? String(d.storeInformation.cashbackRate)
            : "",
        store_websiteUrl: d.storeInformation?.websiteUrl || "",
        store_street: d.storeInformation?.street || "",
        store_housenumber: d.storeInformation?.housenumber || "",
        store_addressAddition: d.storeInformation?.addressAddition || "",
        store_zipCode: d.storeInformation?.zipCode || "",
        store_city: d.storeInformation?.city || "",
        store_country: d.storeInformation?.country || "",
        store_phonePrefix: d.storeInformation?.phonePrefix || "",
        store_phoneNumber: d.storeInformation?.phoneNumber || "",
        store_description: d.storeInformation?.descriptionText || "",
        store_latitude:
          d.storeInformation?.latitude !== undefined
            ? String(d.storeInformation.latitude)
            : "",
        store_longitude:
          d.storeInformation?.longitude !== undefined
            ? String(d.storeInformation.longitude)
            : "",
        // Top-level category
        storeCategory: getCategoryName(d.storeCategory),
        storeSubcategory: getCategoryName(d.storeSubcategory),
        // Store Contact
        storeContact_phonePrefix: d.storeContact?.phonePrefix || "",
        storeContact_phoneNumber: d.storeContact?.phoneNumber || "",
        storeContact_websiteUrl: d.storeContact?.websiteUrl || "",
        storeContact_confirmationType: d.storeContact?.confirmationType || "",
        // Cashback Settings
        cashback_dealerRate:
          d.cashbackSettings?.dealerCashbackRate !== undefined
            ? String(d.cashbackSettings.dealerCashbackRate)
            : "",
        cashback_customerRate:
          d.cashbackSettings?.customerCashbackRate !== undefined
            ? String(d.cashbackSettings.customerCashbackRate)
            : "",
        cashback_confirmationType: d.cashbackSettings?.confirmationType || "",
        // Marketing
        assignedUrl: d.assignedUrl || "",
        marketingSetId: d.marketingSetId || "",
        // Multiple Stores (comma-separated)
        stores_names:
          d.stores
            ?.map((s) => s.storeName)
            .filter(Boolean)
            .join("; ") || "",
        stores_ids:
          d.stores
            ?.map((s) => s.storeId)
            .filter(Boolean)
            .join("; ") || "",
        stores_categories:
          d.stores
            ?.map((s) => getCategoryName(s.storeCategory))
            .filter(Boolean)
            .join("; ") || "",
        stores_subcategories:
          d.stores
            ?.map((s) => getCategoryName(s.storeSubcategory))
            .filter(Boolean)
            .join("; ") || "",
        stores_cashbackRates:
          d.stores
            ?.map((s) =>
              s.cashbackRate !== undefined ? String(s.cashbackRate) : "",
            )
            .filter(Boolean)
            .join("; ") || "",
        stores_statuses:
          d.stores
            ?.map((s) => s.status)
            .filter(Boolean)
            .join("; ") || "",
        // Timestamps
        createdAt: d.createdAt || "",
        updatedAt: d.updatedAt || "",
      }));

      const headers = Object.keys(rows[0]);
      const csvContent = [
        headers.join(","),
        ...rows.map((row: Record<string, string>) =>
          headers
            .map((h) => {
              const val = row[h] || "";
              return val.includes(",") ||
                val.includes('"') ||
                val.includes("\n")
                ? `"${val.replace(/"/g, '""')}"`
                : val;
            })
            .join(","),
        ),
      ].join("\n");

      const blob = new Blob(["\uFEFF" + csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `dealers_export_${
        new Date().toISOString().split("T")[0]
      }.csv`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${allDealers.length} dealers`);
    } catch (error) {
      console.error("Export failed:");
      toast.error("Failed to export dealers");
    } finally {
      setIsExporting(false);
    }
  };

  // Password change handler
  const handleChangePassword = async () => {
    if (!selectedDealer?._id) return;

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      await changePasswordMutation.mutateAsync({
        dealerId: selectedDealer._id,
        newPassword,
      });
      toast.success("Dealer password updated successfully");
      setIsChangingPassword(false);
      setNewPassword("");
      setConfirmPassword("");
      setShowNewPassword(false);
      setShowConfirmPassword(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to update password");
    }
  };

  const getStatusBadge = (status?: string) => {
    const statusLower = status?.toLowerCase() || "active";
    const badges = {
      active:
        "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200",
      inactive: "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300",
      pending:
        "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200",
    };
    return badges[statusLower as keyof typeof badges] || badges.active;
  };

  const renderViewModeButtons = () => (
    <div className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
      <button
        onClick={() => setViewMode("card")}
        className={`p-2 rounded-md transition-colors ${
          viewMode === "card"
            ? "bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow-sm"
            : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
        }`}
        title="Card View"
      >
        <Squares2X2Icon className="h-5 w-5" />
      </button>
      <button
        onClick={() => setViewMode("list")}
        className={`p-2 rounded-md transition-colors ${
          viewMode === "list"
            ? "bg-white dark:bg-gray-600 text-primary-600 dark:text-primary-400 shadow-sm"
            : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
        }`}
        title="List View"
      >
        <ListBulletIcon className="h-5 w-5" />
      </button>
    </div>
  );

  const renderCardView = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {dealers.map((dealer) => (
        <div
          key={dealer._id}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border border-gray-200 dark:border-gray-700 cursor-pointer"
          onClick={() => handleViewDetails(dealer)}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <div className="w-12 h-12 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center flex-shrink-0">
                <BuildingStorefrontIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-lg text-gray-900 dark:text-white truncate">
                  {dealer.companyName || dealer.dealerId}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
                  {dealer.dealerId}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-1 flex-shrink-0">
              {dealer.createdByAutomation && (
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                  AI
                </span>
              )}
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(
                  dealer.status,
                )}`}
              >
                {dealer.status || "Active"}
              </span>
            </div>
          </div>

          <div className="space-y-2 mb-4">
            {dealer.email && (
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <EnvelopeIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                <span className="truncate">{dealer.email}</span>
              </div>
            )}
            {(dealer.phonePrefix || dealer.phoneNumber) && (
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <PhoneIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                <span>
                  {dealer.phonePrefix} {dealer.phoneNumber}
                </span>
              </div>
            )}
            {dealer.country && (
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <MapPinIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                <span>{dealer.country}</span>
              </div>
            )}
            {dealer.totalStores !== undefined && (
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <BuildingStorefrontIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                <span>
                  {dealer.totalStores}{" "}
                  {dealer.totalStores === 1 ? "store" : "stores"}
                </span>
              </div>
            )}
            {(dealer.assignedUrl || dealer.marketingSetId) && (
              <div className="flex flex-col space-y-1 pt-1 border-t border-gray-100 dark:border-gray-700 mt-2">
                <div className="flex items-center text-xs font-medium text-primary-600 dark:text-primary-400">
                  <TagIcon className="h-4 w-4 mr-1 flex-shrink-0" />
                  <span>Marketing Set</span>
                </div>
                {dealer.assignedUrl && (
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate pl-5">
                    {dealer.assignedUrl}
                  </p>
                )}
                {dealer.marketingSetId && !dealer.assignedUrl && (
                  <p className="text-[10px] text-gray-500 dark:text-gray-400 truncate pl-5">
                    ID: {dealer.marketingSetId}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleViewDetails(dealer);
              }}
              className="w-full bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors text-sm"
            >
              View Details
            </button>
          </div>
        </div>
      ))}
    </div>
  );

  const renderListView = () => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Dealer
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Contact
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Location
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Stores
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Marketing
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                onClick={() => handleSort("totalTransactions")}
              >
                <div className="flex items-center space-x-1">
                  <span>Transactions</span>
                  {sortBy === "totalTransactions" && (
                    <span>{sortOrder === "asc" ? "↑" : "↓"}</span>
                  )}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                onClick={() => handleSort("totalSponsoredCustomers")}
              >
                <div className="flex items-center space-x-1">
                  <span>Customers</span>
                  {sortBy === "totalSponsoredCustomers" && (
                    <span>{sortOrder === "asc" ? "↑" : "↓"}</span>
                  )}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {dealers.map((dealer) => (
              <tr
                key={dealer._id}
                className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                onClick={() => handleViewDetails(dealer)}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div>
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {dealer.companyName || "N/A"}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {dealer.dealerId}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900 dark:text-white">
                    {dealer.email}
                  </div>
                  {(dealer.phonePrefix || dealer.phoneNumber) && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {dealer.phonePrefix} {dealer.phoneNumber}
                    </div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {dealer.country || "N/A"}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                  {dealer.stores && dealer.stores.length > 0 ? (
                    <div className="space-y-1">
                      {dealer.stores.map((s, i) => (
                        <div key={s.storeId || i} className="text-xs">
                          <span className="text-gray-900 dark:text-white font-medium">
                            {s.storeName}
                          </span>
                          <div className="text-gray-400 dark:text-gray-500 font-mono text-[10px]">
                            {s.storeId}
                          </div>
                          {s.storeCategory && (
                            <div className="text-[10px] text-primary-600 dark:text-primary-400">
                              {typeof s.storeCategory === "object" &&
                              s.storeCategory !== null
                                ? (s.storeCategory as any).name ||
                                  (s.storeCategory as any).categoryName ||
                                  ""
                                : s.storeCategory}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span>{dealer.totalStores || 0}</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {dealer.assignedUrl || dealer.marketingSetId ? (
                    <div className="flex flex-col">
                      <div className="flex items-center text-primary-600 dark:text-primary-400">
                        <TagIcon className="h-4 w-4 mr-1" />
                        <span className="text-xs font-medium">Assigned</span>
                      </div>
                      <div className="max-w-[150px] truncate text-[10px] text-gray-400">
                        {dealer.assignedUrl || `ID: ${dealer.marketingSetId}`}
                      </div>
                    </div>
                  ) : (
                    <span className="text-gray-300 dark:text-gray-600">-</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-primary-600 dark:text-primary-400">
                  {dealer.totalTransactions || 0}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-600 dark:text-green-400">
                  {dealer.totalSponsoredCustomers || 0}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-1">
                    {dealer.createdByAutomation && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                        AI
                      </span>
                    )}
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                        dealer.status,
                      )}`}
                    >
                      {dealer.status || "Active"}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewDetails(dealer);
                    }}
                    className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300"
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Dealers
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage dealer accounts
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={handleUpdateMetrics}
            disabled={updateMetricsMutation.isPending}
            className="inline-flex items-center px-4 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh transaction and customer counts from engine"
          >
            <ArrowPathIcon
              className={`h-4 w-4 mr-2 ${
                updateMetricsMutation.isPending ? "animate-spin" : ""
              }`}
            />
            {updateMetricsMutation.isPending
              ? "Updating..."
              : "Refresh Transactions Count"}
          </button>
          <button
            onClick={handleExportDealers}
            disabled={isExporting}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
            {isExporting ? "Exporting..." : "Export Dealers"}
          </button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total Dealers
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.total || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Active
            </div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {stats.active || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Inactive
            </div>
            <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">
              {stats.inactive || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Pending
            </div>
            <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">
              {stats.pending || 0}
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0 lg:space-x-4">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <SearchBar
              onSearch={setSearchQuery}
              initialValue={searchQuery}
              placeholder="Search by ID, name, email, store name or Assigned URL..."
            />
          </div>

          {/* Filters and View Mode */}
          <div className="flex items-center space-x-3">
            {/* View Mode Toggle */}
            {renderViewModeButtons()}

            {/* Category Filter */}
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2 min-w-[160px]"
            >
              <option value="">All Categories</option>
              {systemCategories
                ?.filter((c) => c.isActive)
                .sort((a, b) => a.displayOrder - b.displayOrder)
                .map((cat) => (
                  <option key={cat._id} value={cat.categoryName}>
                    {cat.categoryName}
                  </option>
                ))}
            </select>

            {/* Status Filter */}
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as FilterType)}
                className="rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2 min-w-[200px]"
              >
                <optgroup label="Status">
                  <option value="all">All Dealers</option>
                  <option value="active">Active Only</option>
                  <option value="inactive">Inactive Only</option>
                  <option value="pending">Pending Only</option>
                </optgroup>
                <optgroup label="Marketing">
                  <option value="assigned">With Marketing Set</option>
                  <option value="unassigned">Without Marketing Set</option>
                </optgroup>
                <optgroup label="Images">
                  <option value="missing_images">Missing Images</option>
                </optgroup>
              </select>
            </div>

            {/* Automation Filter */}
            <div className="flex items-center space-x-2">
              <select
                value={automationFilter}
                onChange={(e) =>
                  setAutomationFilter(
                    e.target.value as "" | "automation_only" | "non_automation",
                  )
                }
                className="rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2 min-w-[160px]"
              >
                <option value="">All Types</option>
                <option value="automation_only">AI Automated Only</option>
                <option value="non_automation">Manual Only</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Dealers Content */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      ) : dealers.length > 0 ? (
        <div className="space-y-4">
          {viewMode === "card" ? renderCardView() : renderListView()}

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
          />
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center border border-gray-200 dark:border-gray-700">
          <div className="text-gray-400 dark:text-gray-500 mb-3">
            <BuildingStorefrontIcon className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No dealers found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {searchQuery || filter !== "all"
              ? "Try adjusting your search or filters"
              : "No dealers registered yet"}
          </p>
        </div>
      )}

      {/* Dealer Detail Modal */}
      {selectedDealer && (
        <div
          className={`fixed inset-0 z-50 overflow-y-auto ${
            isDetailModalOpen ? "block" : "hidden"
          }`}
        >
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={handleCloseDetailModal}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full">
              {/* Header */}
              <div className="bg-primary-600 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <BuildingStorefrontIcon className="h-8 w-8 text-white" />
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      {selectedDealer.companyName || "Dealer Details"}
                    </h3>
                    <p className="text-sm text-primary-100">
                      {selectedDealer.dealerId}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseDetailModal}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="bg-white dark:bg-gray-800 px-6 py-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="space-y-6">
                  {/* Basic Information */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <BuildingStorefrontIcon className="h-5 w-5 mr-2" />
                      Basic Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Dealer ID
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
                          {selectedDealer.dealerId}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Status
                        </label>
                        <p className="mt-1 flex items-center space-x-1">
                          {selectedDealer.createdByAutomation && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                              AI Automated
                            </span>
                          )}
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(
                              selectedDealer.status,
                            )}`}
                          >
                            {selectedDealer.status || "Active"}
                          </span>
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Company Name
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {selectedDealer.companyName || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Currency
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {selectedDealer.currency || "EUR"}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide flex items-center justify-between">
                          <span>Email</span>
                          {!isEditingEmail && (
                            <button
                              onClick={handleStartEditEmail}
                              className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
                              title="Edit Email"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                        </label>
                        {isEditingEmail ? (
                          <div className="mt-1 flex items-center space-x-2">
                            <input
                              type="email"
                              value={editEmail}
                              onChange={(e) => setEditEmail(e.target.value)}
                              className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              autoFocus
                            />
                            <button
                              onClick={handleSaveEmail}
                              disabled={updateContactMutation.isPending}
                              className="text-green-600 hover:text-green-700 disabled:opacity-50"
                              title="Save"
                            >
                              <CheckIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={handleCancelEditEmail}
                              className="text-red-600 hover:text-red-700"
                              title="Cancel"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </div>
                        ) : (
                          <p className="mt-1 text-sm text-gray-900 dark:text-white break-all">
                            {selectedDealer.email}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide flex items-center justify-between">
                          <span>Phone</span>
                          {!isEditingPhone && (
                            <button
                              onClick={handleStartEditPhone}
                              className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
                              title="Edit Phone"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                        </label>
                        {isEditingPhone ? (
                          <div className="mt-1 flex items-center space-x-2">
                            <input
                              type="text"
                              value={editPhonePrefix}
                              onChange={(e) =>
                                setEditPhonePrefix(e.target.value)
                              }
                              placeholder="+49"
                              className="w-16 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                            <input
                              type="text"
                              value={editPhoneNumber}
                              onChange={(e) =>
                                setEditPhoneNumber(e.target.value)
                              }
                              placeholder="Phone number"
                              className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              autoFocus
                            />
                            <button
                              onClick={handleSavePhone}
                              disabled={updateContactMutation.isPending}
                              className="text-green-600 hover:text-green-700 disabled:opacity-50"
                              title="Save"
                            >
                              <CheckIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={handleCancelEditPhone}
                              className="text-red-600 hover:text-red-700"
                              title="Cancel"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </div>
                        ) : (
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {selectedDealer.phonePrefix ||
                            selectedDealer.phoneNumber
                              ? `${selectedDealer.phonePrefix || ""} ${
                                  selectedDealer.phoneNumber || ""
                                }`
                              : "Not provided"}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide flex items-center justify-between">
                          <span>Country</span>
                          {!isEditingCompanyCountry && (
                            <button
                              onClick={() => {
                                setEditCompanyCountry(
                                  selectedDealer.country || "",
                                );
                                setIsEditingCompanyCountry(true);
                              }}
                              className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                        </label>
                        {isEditingCompanyCountry ? (
                          <div className="mt-1 flex items-center space-x-2">
                            <input
                              type="text"
                              value={editCompanyCountry}
                              onChange={(e) =>
                                setEditCompanyCountry(e.target.value)
                              }
                              className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              autoFocus
                            />
                            <button
                              onClick={async () => {
                                if (!selectedDealer.dealerId) return;
                                try {
                                  await updateDealerCountryMutation.mutateAsync(
                                    {
                                      dealerId: selectedDealer.dealerId,
                                      country: editCompanyCountry,
                                    },
                                  );
                                  setSelectedDealer({
                                    ...selectedDealer,
                                    country: editCompanyCountry,
                                    companyAddress: {
                                      ...selectedDealer.companyAddress,
                                      country: editCompanyCountry,
                                    },
                                  });
                                  setIsEditingCompanyCountry(false);
                                  toast.success("Country updated");
                                } catch {
                                  toast.error("Failed to update country");
                                }
                              }}
                              disabled={updateDealerCountryMutation.isPending}
                              className="text-green-600 hover:text-green-700 disabled:opacity-50"
                            >
                              <CheckIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => setIsEditingCompanyCountry(false)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </div>
                        ) : (
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {selectedDealer.country || "—"}
                          </p>
                        )}
                      </div>
                      {selectedDealer.latitude && selectedDealer.longitude && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Location
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {typeof selectedDealer.latitude === "number"
                              ? selectedDealer.latitude.toFixed(6)
                              : selectedDealer.latitude}
                            ,{" "}
                            {typeof selectedDealer.longitude === "number"
                              ? selectedDealer.longitude.toFixed(6)
                              : selectedDealer.longitude}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Performance Metrics */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <ChartBarIcon className="h-5 w-5 mr-2" />
                      Performance Metrics
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Total Transactions
                        </label>
                        <p className="mt-1 text-2xl font-bold text-primary-600 dark:text-primary-400">
                          {selectedDealer.totalTransactions || 0}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Sponsored Customers
                        </label>
                        <p className="mt-1 text-2xl font-bold text-green-600 dark:text-green-400">
                          {selectedDealer.totalSponsoredCustomers || 0}
                        </p>
                      </div>
                      {selectedDealer.metricsLastUpdated && (
                        <div className="md:col-span-2">
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            Last updated:{" "}
                            {new Date(
                              selectedDealer.metricsLastUpdated,
                            ).toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Marketing Information */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <TagIcon className="h-5 w-5 mr-2" />
                      Marketing Information
                      {selectedDealer.redirects &&
                        selectedDealer.redirects.length > 0 && (
                          <span className="ml-2 text-xs bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 px-2 py-0.5 rounded-full">
                            {selectedDealer.redirects.length} URL
                            {selectedDealer.redirects.length > 1 ? "s" : ""}
                          </span>
                        )}
                    </h4>
                    <div className="space-y-3">
                      {selectedDealer.redirects &&
                      selectedDealer.redirects.length > 0 ? (
                        selectedDealer.redirects.map(
                          (
                            redirect: {
                              assignedUrl: string;
                              marketingSetId?: string;
                              description?: string;
                              isActive?: boolean;
                            },
                            index: number,
                          ) => (
                            <div
                              key={redirect.assignedUrl || index}
                              className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                                      #{index + 1}
                                    </span>
                                    {redirect.marketingSetId && (
                                      <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded">
                                        {redirect.marketingSetId}
                                      </span>
                                    )}
                                    {redirect.isActive === false && (
                                      <span className="text-xs bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 px-2 py-0.5 rounded">
                                        Inactive
                                      </span>
                                    )}
                                  </div>
                                  <p className="mt-1 text-sm text-primary-600 dark:text-primary-400 font-mono break-all">
                                    {redirect.assignedUrl}
                                  </p>
                                  {redirect.description && (
                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                      {redirect.description}
                                    </p>
                                  )}
                                </div>
                                <button
                                  onClick={() => {
                                    navigator.clipboard.writeText(
                                      redirect.assignedUrl || "",
                                    );
                                  }}
                                  className="ml-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 flex-shrink-0"
                                  title="Copy to clipboard"
                                >
                                  <svg
                                    className="h-4 w-4"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                                    />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          ),
                        )
                      ) : (
                        <div className="text-center py-4">
                          <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                            No marketing URLs assigned
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Activation Information */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <CalendarIcon className="h-5 w-5 mr-2" />
                      Activation Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Activation Status
                        </label>
                        <div className="mt-1 flex items-center space-x-2">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              selectedDealer.activationStatus === "activated"
                                ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
                                : selectedDealer.activationStatus === "pending"
                                  ? "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200"
                                  : "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300"
                            }`}
                          >
                            {selectedDealer.activationStatus || "pending"}
                          </span>
                          {selectedDealer.activationStatus !== "activated" &&
                            !showActivateConfirm && (
                              <button
                                onClick={() => setShowActivateConfirm(true)}
                                className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                              >
                                Activate
                              </button>
                            )}
                        </div>
                        {showActivateConfirm &&
                          selectedDealer.activationStatus !== "activated" && (
                            <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600">
                              <label className="flex items-center space-x-2 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={addPrepaidTopup}
                                  onChange={(e) =>
                                    setAddPrepaidTopup(e.target.checked)
                                  }
                                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <span className="text-sm text-gray-700 dark:text-gray-300">
                                  Add 10€ prepaid topup
                                </span>
                              </label>
                              <div className="mt-3 flex items-center space-x-2">
                                <button
                                  onClick={handleActivateDealer}
                                  disabled={activateDealerMutation.isPending}
                                  className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-green-600 text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  {activateDealerMutation.isPending
                                    ? "Activating..."
                                    : "Confirm Activation"}
                                </button>
                                <button
                                  onClick={() => {
                                    setShowActivateConfirm(false);
                                    setAddPrepaidTopup(false);
                                  }}
                                  className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          )}
                      </div>
                      {selectedDealer.activationDate && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Activation Date
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {new Date(
                              selectedDealer.activationDate,
                            ).toLocaleString()}
                          </p>
                        </div>
                      )}
                      {selectedDealer.activationMethod && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Activation Method
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white capitalize">
                            {selectedDealer.activationMethod}
                          </p>
                        </div>
                      )}
                      {selectedDealer.activationVoucherCode && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Voucher Code
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
                            {selectedDealer.activationVoucherCode}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* VAT Information */}
                  {(selectedDealer.vatId ||
                    selectedDealer.vatValidity !== undefined) && (
                    <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        VAT Information
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedDealer.vatId && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              VAT ID
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedDealer.vatId}
                            </p>
                          </div>
                        )}
                        {selectedDealer.vatValidity !== undefined && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              VAT Validity
                            </label>
                            <p className="mt-1">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  selectedDealer.vatValidity
                                    ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200"
                                    : "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200"
                                }`}
                              >
                                {selectedDealer.vatValidity
                                  ? "Valid"
                                  : "Invalid"}
                              </span>
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Company Address */}
                  {selectedDealer.companyAddress && (
                    <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                        <MapPinIcon className="h-5 w-5 mr-2" />
                        Company Address
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedDealer.companyAddress.street && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Street
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedDealer.companyAddress.street}
                            </p>
                          </div>
                        )}
                        {selectedDealer.companyAddress.housenumber && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              House Number
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedDealer.companyAddress.housenumber}
                            </p>
                          </div>
                        )}
                        {selectedDealer.companyAddress.addressAddition && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Address Addition
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedDealer.companyAddress.addressAddition}
                            </p>
                          </div>
                        )}
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide flex items-center justify-between">
                            <span>ZIP Code</span>
                            {!isEditingCompanyZipCode && (
                              <button
                                onClick={() => {
                                  setEditCompanyZipCode(
                                    selectedDealer.companyAddress?.zipCode ||
                                      "",
                                  );
                                  setIsEditingCompanyZipCode(true);
                                }}
                                className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            )}
                          </label>
                          {isEditingCompanyZipCode ? (
                            <div className="mt-1 flex items-center space-x-2">
                              <input
                                type="text"
                                value={editCompanyZipCode}
                                onChange={(e) =>
                                  setEditCompanyZipCode(e.target.value)
                                }
                                className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                autoFocus
                              />
                              <button
                                onClick={async () => {
                                  if (!selectedDealer.dealerId) return;
                                  try {
                                    await updateDealerZipCodeMutation.mutateAsync(
                                      {
                                        dealerId: selectedDealer.dealerId,
                                        zipCode: editCompanyZipCode,
                                      },
                                    );
                                    setSelectedDealer({
                                      ...selectedDealer,
                                      companyAddress: {
                                        ...selectedDealer.companyAddress,
                                        zipCode: editCompanyZipCode,
                                      },
                                    });
                                    setIsEditingCompanyZipCode(false);
                                    toast.success("ZIP code updated");
                                  } catch {
                                    toast.error("Failed to update ZIP code");
                                  }
                                }}
                                disabled={updateDealerZipCodeMutation.isPending}
                                className="text-green-600 hover:text-green-700 disabled:opacity-50"
                              >
                                <CheckIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() =>
                                  setIsEditingCompanyZipCode(false)
                                }
                                className="text-red-600 hover:text-red-700"
                              >
                                <XMarkIcon className="h-5 w-5" />
                              </button>
                            </div>
                          ) : (
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedDealer.companyAddress?.zipCode || "—"}
                            </p>
                          )}
                        </div>
                        {selectedDealer.companyAddress.city && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              City
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedDealer.companyAddress.city}
                            </p>
                          </div>
                        )}
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide flex items-center justify-between">
                            <span>Country</span>
                            {!isEditingCompanyCountry && (
                              <button
                                onClick={() => {
                                  setEditCompanyCountry(
                                    selectedDealer.companyAddress?.country ||
                                      "",
                                  );
                                  setIsEditingCompanyCountry(true);
                                }}
                                className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </button>
                            )}
                          </label>
                          {isEditingCompanyCountry ? (
                            <div className="mt-1 flex items-center space-x-2">
                              <input
                                type="text"
                                value={editCompanyCountry}
                                onChange={(e) =>
                                  setEditCompanyCountry(e.target.value)
                                }
                                className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                autoFocus
                              />
                              <button
                                onClick={async () => {
                                  if (!selectedDealer.dealerId) return;
                                  try {
                                    await updateDealerCountryMutation.mutateAsync(
                                      {
                                        dealerId: selectedDealer.dealerId,
                                        country: editCompanyCountry,
                                      },
                                    );
                                    setSelectedDealer({
                                      ...selectedDealer,
                                      companyAddress: {
                                        ...selectedDealer.companyAddress,
                                        country: editCompanyCountry,
                                      },
                                    });
                                    setIsEditingCompanyCountry(false);
                                    toast.success("Country updated");
                                  } catch {
                                    toast.error("Failed to update country");
                                  }
                                }}
                                disabled={updateDealerCountryMutation.isPending}
                                className="text-green-600 hover:text-green-700 disabled:opacity-50"
                              >
                                <CheckIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() =>
                                  setIsEditingCompanyCountry(false)
                                }
                                className="text-red-600 hover:text-red-700"
                              >
                                <XMarkIcon className="h-5 w-5" />
                              </button>
                            </div>
                          ) : (
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedDealer.companyAddress?.country || "—"}
                            </p>
                          )}
                        </div>
                        {selectedDealer.companyAddress.vatId && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              VAT ID (Address)
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedDealer.companyAddress.vatId}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Contact Person */}
                  {selectedDealer.contactPerson && (
                    <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                        <PhoneIcon className="h-5 w-5 mr-2" />
                        Contact Person
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedDealer.contactPerson.salutation && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Salutation
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedDealer.contactPerson.salutation}
                            </p>
                          </div>
                        )}
                        {selectedDealer.contactPerson.firstName && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              First Name
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedDealer.contactPerson.firstName}
                            </p>
                          </div>
                        )}
                        {selectedDealer.contactPerson.lastName && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Last Name
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedDealer.contactPerson.lastName}
                            </p>
                          </div>
                        )}
                        {selectedDealer.contactPerson.email && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Email
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white break-all">
                              {selectedDealer.contactPerson.email}
                            </p>
                          </div>
                        )}
                        {(selectedDealer.contactPerson.phonePrefix ||
                          selectedDealer.contactPerson.phoneNumber) && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Phone
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedDealer.contactPerson.phonePrefix}{" "}
                              {selectedDealer.contactPerson.phoneNumber}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Store Information */}
                  {selectedDealer.storeInformation && (
                    <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                        <BuildingStorefrontIcon className="h-5 w-5 mr-2" />
                        Store Information
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedDealer.storeInformation.storeName && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Store Name
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedDealer.storeInformation.storeName}
                            </p>
                          </div>
                        )}
                        {selectedDealer.storeInformation.storeCategory && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Category
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {typeof selectedDealer.storeInformation
                                .storeCategory === "object" &&
                              selectedDealer.storeInformation.storeCategory !==
                                null
                                ? (
                                    selectedDealer.storeInformation
                                      .storeCategory as any
                                  ).name ||
                                  (
                                    selectedDealer.storeInformation
                                      .storeCategory as any
                                  ).categoryName ||
                                  "Category"
                                : selectedDealer.storeInformation.storeCategory}
                            </p>
                          </div>
                        )}
                        {selectedDealer.storeInformation.storeSubcategory && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Subcategory
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {typeof selectedDealer.storeInformation
                                .storeSubcategory === "object" &&
                              selectedDealer.storeInformation
                                .storeSubcategory !== null
                                ? (
                                    selectedDealer.storeInformation
                                      .storeSubcategory as any
                                  ).name || "Subcategory"
                                : selectedDealer.storeInformation
                                    .storeSubcategory}
                            </p>
                          </div>
                        )}
                        {selectedDealer.storeInformation.cashbackRate !==
                          undefined && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Cashback Rate
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedDealer.storeInformation.cashbackRate}%
                            </p>
                          </div>
                        )}
                        {selectedDealer.storeInformation.websiteUrl && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Website
                            </label>
                            <p className="mt-1 text-sm text-blue-600 dark:text-blue-400 break-all">
                              <a
                                href={
                                  selectedDealer.storeInformation.websiteUrl
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:underline"
                              >
                                {selectedDealer.storeInformation.websiteUrl}
                              </a>
                            </p>
                          </div>
                        )}
                        {(selectedDealer.storeInformation.phonePrefix ||
                          selectedDealer.storeInformation.phoneNumber) && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Store Phone
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedDealer.storeInformation.phonePrefix}{" "}
                              {selectedDealer.storeInformation.phoneNumber}
                            </p>
                          </div>
                        )}
                        {selectedDealer.storeInformation.descriptionText && (
                          <div className="md:col-span-2">
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Description
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white whitespace-pre-wrap">
                              {selectedDealer.storeInformation.descriptionText}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Multiple Stores */}
                  {selectedDealer.stores &&
                    selectedDealer.stores.length > 0 && (
                      <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                        <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                          <BuildingStorefrontIcon className="h-5 w-5 mr-2" />
                          Stores ({selectedDealer.stores.length})
                        </h4>
                        <div className="space-y-4">
                          {selectedDealer.stores.map((store, index) => (
                            <div
                              key={store.storeId || index}
                              className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4"
                            >
                              <div className="flex items-center justify-between mb-3">
                                <h5 className="font-semibold text-gray-900 dark:text-white">
                                  {store.storeName}
                                </h5>
                                <select
                                  value={store.status || "active"}
                                  onChange={(e) =>
                                    handleStoreStatusChange(
                                      store.storeId,
                                      e.target.value,
                                    )
                                  }
                                  disabled={updateStoreStatusMutation.isPending}
                                  className={`px-3 py-1.5 pr-8 rounded-lg text-xs font-medium cursor-pointer transition-colors appearance-none bg-no-repeat bg-right ${
                                    store.status === "active"
                                      ? "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 border border-green-300 dark:border-green-700"
                                      : store.status === "pending"
                                        ? "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 border border-yellow-300 dark:border-yellow-700"
                                        : "bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-500"
                                  } disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80`}
                                  style={{
                                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='currentColor'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                    backgroundSize: "16px",
                                    backgroundPosition: "right 4px center",
                                  }}
                                >
                                  <option value="active">Active</option>
                                  <option value="pending">Pending</option>
                                  <option value="inactive">Inactive</option>
                                </select>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                {store.storeId && (
                                  <div>
                                    <span className="text-gray-500 dark:text-gray-400">
                                      Store ID:{" "}
                                    </span>
                                    <span className="text-gray-900 dark:text-white font-mono">
                                      {store.storeId}
                                    </span>
                                  </div>
                                )}
                                {store.cashbackRate !== undefined && (
                                  <div>
                                    <span className="text-gray-500 dark:text-gray-400">
                                      Cashback:{" "}
                                    </span>
                                    <span className="text-gray-900 dark:text-white">
                                      {store.cashbackRate}%
                                    </span>
                                  </div>
                                )}
                                {editingCategoryStoreId === store.storeId ? (
                                  <div className="md:col-span-2 space-y-2">
                                    <div className="flex items-center gap-2">
                                      <span className="text-gray-500 dark:text-gray-400 text-xs w-20 shrink-0">
                                        Category:
                                      </span>
                                      <select
                                        value={editStoreCategory}
                                        onChange={(e) => {
                                          setEditStoreCategory(e.target.value);
                                          setEditStoreSubcategory("");
                                        }}
                                        className="flex-1 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm p-1"
                                      >
                                        <option value="">-- None --</option>
                                        {systemCategories
                                          ?.filter((c) => c.isActive)
                                          .sort(
                                            (a, b) =>
                                              a.displayOrder - b.displayOrder,
                                          )
                                          .map((cat) => (
                                            <option
                                              key={cat._id}
                                              value={cat.categoryName}
                                            >
                                              {cat.categoryName}
                                            </option>
                                          ))}
                                      </select>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-gray-500 dark:text-gray-400 text-xs w-20 shrink-0">
                                        Subcategory:
                                      </span>
                                      <select
                                        value={editStoreSubcategory}
                                        onChange={(e) =>
                                          setEditStoreSubcategory(
                                            e.target.value,
                                          )
                                        }
                                        className="flex-1 rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm p-1"
                                      >
                                        <option value="">-- None --</option>
                                        {systemCategories
                                          ?.find(
                                            (c) =>
                                              c.categoryName ===
                                              editStoreCategory,
                                          )
                                          ?.subcategories?.map((sub, i) => {
                                            const subName =
                                              typeof sub === "object"
                                                ? sub.name
                                                : sub;
                                            return (
                                              <option key={i} value={subName}>
                                                {subName}
                                              </option>
                                            );
                                          })}
                                      </select>
                                    </div>
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() =>
                                          handleSaveCategory(store.storeId)
                                        }
                                        disabled={
                                          updateStoreCategoryMutation.isPending
                                        }
                                        className="px-2 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
                                      >
                                        {updateStoreCategoryMutation.isPending
                                          ? "Saving..."
                                          : "Save"}
                                      </button>
                                      <button
                                        onClick={handleCancelEditCategory}
                                        className="px-2 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    <div>
                                      <span className="text-gray-500 dark:text-gray-400">
                                        Category:{" "}
                                      </span>
                                      <span className="text-gray-900 dark:text-white">
                                        {getCategoryNameStr(
                                          store.storeCategory,
                                        ) || (
                                          <span className="text-gray-400 italic">
                                            Not set
                                          </span>
                                        )}
                                      </span>
                                      <button
                                        onClick={() =>
                                          handleStartEditCategory(store)
                                        }
                                        className="ml-2 text-gray-400 hover:text-primary-600 dark:hover:text-primary-400"
                                        title="Edit category"
                                      >
                                        <PencilIcon className="h-3.5 w-3.5 inline" />
                                      </button>
                                    </div>
                                    {store.storeSubcategory && (
                                      <div>
                                        <span className="text-gray-500 dark:text-gray-400">
                                          Subcategory:{" "}
                                        </span>
                                        <span className="text-gray-900 dark:text-white">
                                          {getCategoryNameStr(
                                            store.storeSubcategory,
                                          )}
                                        </span>
                                      </div>
                                    )}
                                  </>
                                )}
                                {store.storeType && (
                                  <div>
                                    <span className="text-gray-500 dark:text-gray-400">
                                      Type:{" "}
                                    </span>
                                    <span className="text-gray-900 dark:text-white capitalize">
                                      {store.storeType}
                                    </span>
                                  </div>
                                )}
                                {(store.street || store.city) && (
                                  <div className="md:col-span-2">
                                    <span className="text-gray-500 dark:text-gray-400">
                                      Address:{" "}
                                    </span>
                                    <span className="text-gray-900 dark:text-white">
                                      {[
                                        store.street,
                                        store.housenumber,
                                        store.zipCode,
                                        store.city,
                                        store.country,
                                      ]
                                        .filter(Boolean)
                                        .join(", ")}
                                    </span>
                                  </div>
                                )}
                                <div className="md:col-span-2">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-gray-500 dark:text-gray-400 text-sm">
                                      Country:
                                    </span>
                                    {editingStoreCountryId === store.storeId ? (
                                      <>
                                        <input
                                          type="text"
                                          value={editStoreCountry}
                                          onChange={(e) =>
                                            setEditStoreCountry(e.target.value)
                                          }
                                          className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-0.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-28"
                                          autoFocus
                                        />
                                        <button
                                          onClick={async () => {
                                            try {
                                              await updateStoreCountryMutation.mutateAsync(
                                                {
                                                  dealerId:
                                                    selectedDealer.dealerId,
                                                  storeId: store.storeId!,
                                                  country: editStoreCountry,
                                                },
                                              );
                                              setSelectedDealer({
                                                ...selectedDealer,
                                                stores:
                                                  selectedDealer.stores!.map(
                                                    (s) =>
                                                      s.storeId ===
                                                      store.storeId
                                                        ? {
                                                            ...s,
                                                            country:
                                                              editStoreCountry,
                                                          }
                                                        : s,
                                                  ),
                                              });
                                              setEditingStoreCountryId(null);
                                              toast.success(
                                                "Store country updated",
                                              );
                                            } catch {
                                              toast.error(
                                                "Failed to update store country",
                                              );
                                            }
                                          }}
                                          disabled={
                                            updateStoreCountryMutation.isPending
                                          }
                                          className="text-green-600 hover:text-green-700 disabled:opacity-50"
                                        >
                                          <CheckIcon className="h-4 w-4" />
                                        </button>
                                        <button
                                          onClick={() =>
                                            setEditingStoreCountryId(null)
                                          }
                                          className="text-red-600 hover:text-red-700"
                                        >
                                          <XMarkIcon className="h-4 w-4" />
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <span className="text-gray-900 dark:text-white text-sm">
                                          {store.country || "—"}
                                        </span>
                                        <button
                                          onClick={() => {
                                            setEditStoreCountry(
                                              store.country || "",
                                            );
                                            setEditingStoreCountryId(
                                              store.storeId!,
                                            );
                                          }}
                                          className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
                                        >
                                          <PencilIcon className="h-3.5 w-3.5" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <div className="md:col-span-2">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-gray-500 dark:text-gray-400 text-sm">
                                      ZIP:
                                    </span>
                                    {editingStoreZipCodeId === store.storeId ? (
                                      <>
                                        <input
                                          type="text"
                                          value={editStoreZipCode}
                                          onChange={(e) =>
                                            setEditStoreZipCode(e.target.value)
                                          }
                                          className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-0.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-28"
                                          autoFocus
                                        />
                                        <button
                                          onClick={async () => {
                                            try {
                                              await updateStoreZipCodeMutation.mutateAsync(
                                                {
                                                  dealerId:
                                                    selectedDealer.dealerId,
                                                  storeId: store.storeId!,
                                                  zipCode: editStoreZipCode,
                                                },
                                              );
                                              setSelectedDealer({
                                                ...selectedDealer,
                                                stores:
                                                  selectedDealer.stores!.map(
                                                    (s) =>
                                                      s.storeId ===
                                                      store.storeId
                                                        ? {
                                                            ...s,
                                                            zipCode:
                                                              editStoreZipCode,
                                                          }
                                                        : s,
                                                  ),
                                              });
                                              setEditingStoreZipCodeId(null);
                                              toast.success(
                                                "Store ZIP updated",
                                              );
                                            } catch {
                                              toast.error(
                                                "Failed to update store ZIP",
                                              );
                                            }
                                          }}
                                          disabled={
                                            updateStoreZipCodeMutation.isPending
                                          }
                                          className="text-green-600 hover:text-green-700 disabled:opacity-50"
                                        >
                                          <CheckIcon className="h-4 w-4" />
                                        </button>
                                        <button
                                          onClick={() =>
                                            setEditingStoreZipCodeId(null)
                                          }
                                          className="text-red-600 hover:text-red-700"
                                        >
                                          <XMarkIcon className="h-4 w-4" />
                                        </button>
                                      </>
                                    ) : (
                                      <>
                                        <span className="text-gray-900 dark:text-white text-sm">
                                          {store.zipCode || "—"}
                                        </span>
                                        <button
                                          onClick={() => {
                                            setEditStoreZipCode(
                                              store.zipCode || "",
                                            );
                                            setEditingStoreZipCodeId(
                                              store.storeId!,
                                            );
                                          }}
                                          className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
                                        >
                                          <PencilIcon className="h-3.5 w-3.5" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Opening Hours */}
                              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                    Opening Hours
                                  </span>
                                  {store.businessHours?.timezone && (
                                    <span className="text-xs text-gray-400 dark:text-gray-500">
                                      {store.businessHours.timezone}
                                    </span>
                                  )}
                                </div>
                                {store.businessHours?.regularHours?.length ? (
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1">
                                    {[...store.businessHours.regularHours]
                                      .sort(
                                        (a, b) =>
                                          ((a.dayOfWeek + 6) % 7) -
                                          ((b.dayOfWeek + 6) % 7),
                                      )
                                      .map((hours) => (
                                        <div
                                          key={hours.dayOfWeek}
                                          className="flex justify-between gap-3 text-sm"
                                        >
                                          <span className="text-gray-500 dark:text-gray-400">
                                            {WEEKDAYS[hours.dayOfWeek] ||
                                              `Day ${hours.dayOfWeek}`}
                                          </span>
                                          <div className="text-right">
                                            <span className="text-gray-900 dark:text-white">
                                              {hours.isOpen
                                                ? `${hours.openTime || "—"} – ${
                                                    hours.closeTime || "—"
                                                  }`
                                                : "Closed"}
                                            </span>
                                            {hours.isOpen &&
                                              hours.breakHours?.map(
                                                (breakHour, breakIndex) => (
                                                  <div
                                                    key={breakIndex}
                                                    className="text-xs text-gray-400 dark:text-gray-500"
                                                  >
                                                    Break {breakHour.startTime}{" "}
                                                    – {breakHour.endTime}
                                                  </div>
                                                ),
                                              )}
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                ) : (
                                  <span className="text-sm text-gray-400 dark:text-gray-500 italic">
                                    Not configured
                                  </span>
                                )}
                              </div>

                              {/* Store Images */}
                              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                    Images
                                  </span>
                                  {editingImageStoreId !== store.storeId && (
                                    <button
                                      onClick={() =>
                                        handleStartEditImages(store)
                                      }
                                      className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
                                      title="Edit Images"
                                    >
                                      <PencilIcon className="h-4 w-4" />
                                    </button>
                                  )}
                                </div>
                                {editingImageStoreId === store.storeId ? (
                                  <div className="space-y-2">
                                    <div>
                                      <label className="text-xs text-gray-500 dark:text-gray-400">
                                        Cover Image URL
                                      </label>
                                      <input
                                        type="text"
                                        value={editCoverImage}
                                        onChange={(e) =>
                                          setEditCoverImage(e.target.value)
                                        }
                                        placeholder="https://..."
                                        className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white mt-1"
                                      />
                                    </div>
                                    <div>
                                      <label className="text-xs text-gray-500 dark:text-gray-400">
                                        Logo Image URL
                                      </label>
                                      <input
                                        type="text"
                                        value={editLogoImage}
                                        onChange={(e) =>
                                          setEditLogoImage(e.target.value)
                                        }
                                        placeholder="https://..."
                                        className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white mt-1"
                                      />
                                    </div>
                                    <div className="flex items-center space-x-2 pt-1">
                                      <button
                                        onClick={() =>
                                          handleSaveImages(store.storeId)
                                        }
                                        disabled={
                                          updateStoreImagesMutation.isPending
                                        }
                                        className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                                      >
                                        <CheckIcon className="h-4 w-4 mr-1" />
                                        Save
                                      </button>
                                      <button
                                        onClick={handleCancelEditImages}
                                        className="inline-flex items-center px-3 py-1 rounded-md text-xs font-medium bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-500"
                                      >
                                        <XMarkIcon className="h-4 w-4 mr-1" />
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        Cover
                                      </span>
                                      {store.coverImage ? (
                                        <img
                                          src={store.coverImage}
                                          alt="Cover"
                                          className="mt-1 w-full h-16 object-cover rounded border border-gray-200 dark:border-gray-600"
                                        />
                                      ) : (
                                        <div className="mt-1 w-full h-16 rounded border border-dashed border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                                          <span className="text-xs text-red-500 dark:text-red-400">
                                            Missing
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    <div>
                                      <span className="text-xs text-gray-500 dark:text-gray-400">
                                        Logo
                                      </span>
                                      {store.logoImage ? (
                                        <img
                                          src={store.logoImage}
                                          alt="Logo"
                                          className="mt-1 w-full h-16 object-contain rounded border border-gray-200 dark:border-gray-600"
                                        />
                                      ) : (
                                        <div className="mt-1 w-full h-16 rounded border border-dashed border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
                                          <span className="text-xs text-red-500 dark:text-red-400">
                                            Missing
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Cashback Settings */}
                  {selectedDealer.cashbackSettings && (
                    <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Cashback Settings
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {selectedDealer.cashbackSettings.dealerCashbackRate !==
                          undefined && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Dealer Cashback Rate
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {
                                selectedDealer.cashbackSettings
                                  .dealerCashbackRate
                              }
                              %
                            </p>
                          </div>
                        )}
                        {selectedDealer.cashbackSettings
                          .customerCashbackRate !== undefined && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Customer Cashback Rate
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {
                                selectedDealer.cashbackSettings
                                  .customerCashbackRate
                              }
                              %
                            </p>
                          </div>
                        )}
                        {selectedDealer.cashbackSettings.confirmationType && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                              Confirmation Type
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white capitalize">
                              {selectedDealer.cashbackSettings.confirmationType}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Sponsor & Backoffice Information */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Sponsor & Backoffice
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Sponsor ID — inline edit */}
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide flex items-center justify-between">
                          <span>Sponsor ID</span>
                          {!isEditingDealerSponsorId && (
                            <button
                              onClick={() => {
                                setEditDealerSponsorId(
                                  selectedDealer.sponsorId || "",
                                );
                                setIsEditingDealerSponsorId(true);
                              }}
                              className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          )}
                        </label>
                        {isEditingDealerSponsorId ? (
                          <div className="mt-1 flex items-center space-x-2">
                            <input
                              type="text"
                              value={editDealerSponsorId}
                              onChange={(e) =>
                                setEditDealerSponsorId(e.target.value)
                              }
                              className="flex-1 text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono"
                              autoFocus
                              placeholder="Sales rep agentId"
                            />
                            <button
                              onClick={async () => {
                                if (!selectedDealer.dealerId) return;
                                try {
                                  await updateDealerSponsorMutation.mutateAsync(
                                    {
                                      dealerId: selectedDealer.dealerId,
                                      sponsorId: editDealerSponsorId,
                                    },
                                  );
                                  setSelectedDealer({
                                    ...selectedDealer,
                                    sponsorId: editDealerSponsorId,
                                  });
                                  setIsEditingDealerSponsorId(false);
                                  toast.success("Sponsor ID updated");
                                } catch {
                                  toast.error("Failed to update Sponsor ID");
                                }
                              }}
                              disabled={updateDealerSponsorMutation.isPending}
                              className="text-green-600 hover:text-green-700 disabled:opacity-50"
                            >
                              <CheckIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => setIsEditingDealerSponsorId(false)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <XMarkIcon className="h-5 w-5" />
                            </button>
                          </div>
                        ) : (
                          <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
                            {selectedDealer.sponsorId || "—"}
                          </p>
                        )}
                      </div>

                      {/* Sponsor Type — readonly (dealers always sales_rep) */}
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                          Sponsor Type
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white capitalize">
                          {selectedDealer.sponsorType
                            ? selectedDealer.sponsorType.replace("_", " ")
                            : "sales rep"}
                        </p>
                      </div>
                      {selectedDealer.dealerIdSalesBackoffice && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Sales Backoffice ID
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
                            {selectedDealer.dealerIdSalesBackoffice}
                          </p>
                        </div>
                      )}
                      {selectedDealer.dealerAgentIdBackoffice && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Agent Backoffice ID
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">
                            {selectedDealer.dealerAgentIdBackoffice}
                          </p>
                        </div>
                      )}
                      {selectedDealer.qrLink && (
                        <div className="md:col-span-2">
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            QR Link
                          </label>
                          <p className="mt-1 text-sm text-blue-600 dark:text-blue-400 break-all">
                            <a
                              href={selectedDealer.qrLink}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:underline"
                            >
                              {selectedDealer.qrLink}
                            </a>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Store Statistics */}
                  {selectedDealer.totalStores !== undefined && (
                    <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                        Statistics
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Total Stores
                          </label>
                          <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                            {selectedDealer.totalStores || 0}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Admin Password Change */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                      <LockClosedIcon className="h-5 w-5 mr-2" />
                      Change Password
                    </h4>
                    {!isChangingPassword ? (
                      <button
                        onClick={() => setIsChangingPassword(true)}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                      >
                        <KeyIcon className="h-4 w-4 mr-2" />
                        Set New Password
                      </button>
                    ) : (
                      <div className="space-y-3 max-w-md">
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            New Password
                          </label>
                          <div className="mt-1 relative">
                            <input
                              type={showNewPassword ? "text" : "password"}
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder="Enter new password (min 6 characters)"
                              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white pr-10"
                              autoFocus
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setShowNewPassword(!showNewPassword)
                              }
                              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            >
                              {showNewPassword ? (
                                <EyeSlashIcon className="h-4 w-4" />
                              ) : (
                                <EyeIcon className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Confirm Password
                          </label>
                          <div className="mt-1 relative">
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              value={confirmPassword}
                              onChange={(e) =>
                                setConfirmPassword(e.target.value)
                              }
                              placeholder="Confirm new password"
                              className="w-full text-sm border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white pr-10"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setShowConfirmPassword(!showConfirmPassword)
                              }
                              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                            >
                              {showConfirmPassword ? (
                                <EyeSlashIcon className="h-4 w-4" />
                              ) : (
                                <EyeIcon className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                        {newPassword &&
                          confirmPassword &&
                          newPassword !== confirmPassword && (
                            <p className="text-xs text-red-500">
                              Passwords do not match
                            </p>
                          )}
                        {newPassword &&
                          newPassword.length > 0 &&
                          newPassword.length < 6 && (
                            <p className="text-xs text-red-500">
                              Password must be at least 6 characters
                            </p>
                          )}
                        <div className="flex items-center space-x-3 pt-1">
                          <button
                            onClick={handleChangePassword}
                            disabled={
                              changePasswordMutation.isPending ||
                              !newPassword ||
                              !confirmPassword ||
                              newPassword !== confirmPassword ||
                              newPassword.length < 6
                            }
                            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {changePasswordMutation.isPending
                              ? "Updating..."
                              : "Update Password"}
                          </button>
                          <button
                            onClick={() => {
                              setIsChangingPassword(false);
                              setNewPassword("");
                              setConfirmPassword("");
                              setShowNewPassword(false);
                              setShowConfirmPassword(false);
                            }}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Timestamps */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Timestamps
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedDealer.createdAt && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Created At
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {new Date(
                              selectedDealer.createdAt,
                            ).toLocaleString()}
                          </p>
                        </div>
                      )}
                      {selectedDealer.updatedAt && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                            Updated At
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {new Date(
                              selectedDealer.updatedAt,
                            ).toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dealers;
